import { observer } from 'mobx-react-lite';
import { startDateLabel } from 'owa-locstrings/lib/strings/startdatelabel.locstring.json';
import { start } from './CustomRecurrenceModalBody.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import createRecurrenceFromSelectedDays from 'owa-recurrence-utils/lib/createRecurrenceFromSelectedDays';
import getRecurrenceEndDate from 'owa-recurrence-utils/lib/getRecurrenceEndDate';
import getRecurrenceInterval from 'owa-recurrence-utils/lib/getRecurrenceInterval';
import getSelectedDaysFromRecurrence from 'owa-recurrence-utils/lib/getSelectedDaysFromRecurrence';

import RecurrenceChoiceComponent from 'owa-recurrence/lib/components/RecurrenceChoiceComponent';
import RecurrenceInterval from 'owa-recurrence/lib/components/RecurrenceInterval';
import RecurrenceSummary from 'owa-recurrence/lib/components/RecurrenceSummary';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import { ControlIcons } from 'owa-control-icons';
import { OwaDate } from 'owa-datetime';
import { DropdownDatePicker } from 'owa-datepicker';
import { getPalette } from 'owa-theme';
import { Icon } from '@fluentui/react/lib';
import { CalendarEvent } from 'owa-calendar-types';
import RecurrenceDaysComponent from 'owa-recurrence/lib/components/RecurrenceDaysComponent';
import {
    isYearlyRecurrence,
    isMonthlyRecurrence,
    isDailyRecurrence,
    isWeeklyRecurrence,
} from 'owa-recurrence-utils/lib/checkRecurrenceTypes';

const styles = require('./CustomRecurrenceModal.scss');

export interface CustomRecurrenceModalBodyProps {
    event: CalendarEvent;
    recurrence: RecurrenceType;
    startDate: OwaDate;
    colorScheme: ColorScheme;
    onRecurrenceChangedCallback: (recurrence: RecurrenceType) => void;
    onStartDateChangedCallback: (startDate: OwaDate) => void;
}

const CustomRecurrenceModalBody: React.FunctionComponent<CustomRecurrenceModalBodyProps> = props => {
    const {
        event,
        recurrence,
        startDate,
        colorScheme,
        onRecurrenceChangedCallback,
        onStartDateChangedCallback,
    } = props;

    const onDaySelectionChangedCallback = React.useCallback(
        (selectedDays: string[]) => {
            let interval = getRecurrenceInterval(recurrence);
            onRecurrenceChangedCallback(
                createRecurrenceFromSelectedDays(
                    startDate,
                    selectedDays,
                    interval,
                    getRecurrenceEndDate(startDate.tz, recurrence)
                )
            );
        },
        [recurrence, startDate, onRecurrenceChangedCallback]
    );

    const daysColor = {
        checkedColor: colorScheme ? colorScheme.calPrimary : getPalette().themePrimary,
        checkedHoverColor: colorScheme ? colorScheme.calDarkAlt : getPalette().themeDarkAlt,
    };

    return (
        <div className={styles.customRecurrenceBodyContainer}>
            <div className={styles.timepickerRow}>
                <span className={styles.startDateText}>{loc(start)}</span>
                <div className={styles.customRecurrenceDatePicker}>
                    <DropdownDatePicker
                        value={startDate}
                        onSelectDate={onStartDateChangedCallback}
                        ariaLabel={loc(startDateLabel)}
                    />
                </div>
            </div>
            <div className={styles.recurrenceRow}>
                <Icon iconName={ControlIcons.SyncOccurence} className={styles.recurrenceRowIcon} />
                <div className={styles.recurrenceControls}>
                    <RecurrenceInterval
                        recurrence={recurrence}
                        startDate={startDate}
                        onRecurrenceChangedCallback={onRecurrenceChangedCallback}
                        colorScheme={colorScheme}
                        className={styles.recurrenceInterval}
                    />
                    {((isDailyRecurrence(recurrence) && getRecurrenceInterval(recurrence) === 1) ||
                        isWeeklyRecurrence(recurrence)) && (
                        <div>
                            <RecurrenceDaysComponent
                                colors={daysColor}
                                className={styles.recurrenceDaysControl}
                                selectedDays={getSelectedDaysFromRecurrence(recurrence)}
                                onDaySelectionChangedCallback={onDaySelectionChangedCallback}
                            />
                        </div>
                    )}
                    {(isYearlyRecurrence(recurrence) || isMonthlyRecurrence(recurrence)) && (
                        <div>
                            <RecurrenceChoiceComponent
                                start={startDate}
                                timeZoneId={startDate.tz}
                                recurrence={recurrence}
                                onSelectionChangedCallback={onRecurrenceChangedCallback}
                                className={styles.recurrenceChoiceControl}
                            />
                        </div>
                    )}
                    {recurrence && (
                        <RecurrenceSummary
                            colorScheme={colorScheme}
                            timeZoneId={startDate.tz}
                            event={event}
                            recurrence={recurrence}
                            onEndRecurrenceChangedCallback={onRecurrenceChangedCallback}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default observer(CustomRecurrenceModalBody);
